import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { DocumentFilterKey, DocumentSort } from '../components/documents-hub/documents-hub.types';
import {
    IClosingAccountOption,
    IClosingDocumentDetail,
    IClosingDocumentListResponse,
    ISetAssignmentRequest,
    ISharepointFetchResponse,
    ISyncRun,
    ISyncStartResult,
    IUploadFailure,
} from '../models/closing-document.model';

/**
 * REV-21376 — the first real ABCP data path (`closing-accounts-tab.page.ts` is still a
 * `/** DUMMY DATA PAGE *\/`).
 *
 * ⚠️ THE ASSIGNMENT WRITES MOVED IN (2026-08-20, TL decision). They were deliberately absent so that
 * wiring them would not compile — that guard held for the whole of the ticket's original scope and
 * came off as a decision, not by accident.
 *
 * #84 — `acceptProposal` / `rejectProposal` answer with the UPDATED document, like `assignDocument`,
 * and therefore run through the SAME success path: `loadDocumentSuccess` + `loadDocuments` (F2). A
 * write failure of THIS slice reports `proposalFailure`, never `loadDocumentsFailure` — that action
 * means "the list/document could not be LOADED" and takes the surface the user is working on away
 * from them (F5). `unassignDocument`'s failure moved to the same action for the identical reason.
 */
export const closingDocumentActions = createActionGroup({
    source: 'ABCP Closing Documents',
    events: {
        /**
         * `filter` AND `search` both ride along rather than being read from state inside the effect:
         * the effect must load exactly what was asked for, or a fast second click resolves against the
         * wrong one.
         *
         * ⚠️ `search` and `sort` are not optional, and that is the point. The server recomputes the
         * THREE COUNTS over the term and ORDERS the rows, so a load that omits either silently
         * un-narrows the pills or throws the table back to newest-first while the header still shows
         * the sort arrow. Making them required turns every forgotten reload site into a compile error
         * instead of a wrong screen. Callers that do not know the current values dispatch
         * `Reload Documents` instead.
         *
         * ⚠️ `assignedBy` (REV-21901 N3/Weg 1, W3) is required for the identical reason: the facet is
         * a SERVER parameter now, and a load that forgot it would silently un-narrow the list under a
         * toggle that still shows "an". `null` is its "off" — like `sort`.
         */
        'Load Documents': props<{
            projectId: string;
            filter: DocumentFilterKey;
            search: string;
            sort: DocumentSort | null;
            assignedBy: 'agent' | null;
        }>(),
        'Load Documents Success': props<{ response: IClosingDocumentListResponse }>(),
        'Load Documents Failure': emptyProps(),
        /**
         * Re-ask for the CURRENT pill, search term and sort, which the slice already holds.
         *
         * This is what a caller dispatches when it knows WHICH closing but not WHAT is on screen —
         * and that is every caller that is not itself the gesture being answered:
         *
         *   · the tab page on mount. It remounts on every return from the single-document route, and
         *     a hardcoded `'inbox'` + `''` there would reset the pill and un-narrow the counts under a
         *     search box that still reads "Bilanz".
         *   · the SharePoint settings modal closing, which tells nobody what changed — the container
         *     persists each binding itself and emits nothing (ADR-0009 §15).
         *
         * `projectId` IS carried, because on the very first load the slice does not know it yet.
         */
        'Reload Documents': props<{ projectId: string }>(),

        'Load Document': props<{ projectId: string; documentId: string }>(),
        'Load Document Success': props<{ document: IClosingDocumentDetail }>(),
        'Load Document Failure': emptyProps(),
        /** Leaving the single-document route, so a stale document cannot flash on the next one. */
        'Clear Document': emptyProps(),

        /**
         * The modal has already validated locally; these are the accepted files only.
         *
         * `accountId` comes from the account page only: a document uploaded there belongs to that
         * account, and the server files it there in the same request.
         */
        'Upload Documents': props<{ projectId: string; files: File[]; accountId?: string }>(),
        /**
         * Partial success is the normal case: some files land, some are refused per file.
         *
         * ⚠️ `succeededFilenames` (#162, AP8a) is what the post-upload poll (`pollAfterUpload$`) watches
         * for a proposal: the FILENAMES the upload actually accepted, i.e. every file minus the ones
         * named in `failures`. Carried here rather than re-derived, because the reducer that resets the
         * poll's watch-list on this action is the only place both lists are in hand together.
         */
        'Upload Documents Settled': props<{ failures: IUploadFailure[]; succeededFilenames: string[] }>(),
        /**
         * The post-upload poll (#162, AP8a) stopped: every just-uploaded file has left `open`
         * (proposed, assigned, or notRelevant), or the tick ceiling was reached.
         */
        'Upload Poll Settled': emptyProps(),

        'Set Not Relevant': props<{ projectId: string; documentId: string; notRelevant: boolean }>(),
        /**
         * REV-22366 — a row write (Nicht relevant, choosing an account) was saved: the row waits for the
         * list that shows it, then leaves its loading state.
         */
        'Row Write Succeeded': props<{ documentId: string }>(),
        /** REV-22366 — a row write was refused: the row leaves its loading state and shows its value again. */
        'Row Write Failed': props<{ documentId: string }>(),
        /**
         * A write that changes nothing in the list (validity) is done: the document leaves
         * its running state at once, it does not wait for a list.
         */
        'Document Write Succeeded': props<{ documentId: string }>(),

        /**
         * The documents of ONE account, for the single-account page.
         *
         * Its own action and its own slot in the state, deliberately: the hub's list is whatever pill
         * is open, and having the account page reuse it would either show the account the hub's rows
         * or leave the hub showing one account's after a visit.
         */
        'Load Account Documents': props<{ projectId: string; accountId: string }>(),
        'Load Account Documents Success': props<{ response: IClosingDocumentListResponse }>(),
        'Load Account Documents Failure': emptyProps(),

        /** The account plan both pickers list. Loaded once per closing, not per picker open. */
        /**
         * "The picker needs this closing's plan" — a request, not a command to fetch.
         *
         * No reducer handler on purpose: reducers run before effects, so a handler that set
         * `accountsStatus: 'loading'` would erase what the guard needs to read. Its effect decides.
         */
        'Load Accounts If Needed': props<{ projectId: string }>(),
        'Load Accounts': props<{ projectId: string }>(),
        'Load Accounts Success': props<{ accounts: IClosingAccountOption[] }>(),
        'Load Accounts Failure': emptyProps(),

        /**
         * File a document to an account or the Projekt-Akte. ONE action for both targets, because the
         * server takes one call: two actions would let a caller wire only half the picker.
         */
        'Assign Document': props<{
            projectId: string;
            documentId: string;
            request: ISetAssignmentRequest;
        }>(),
        /** Back to Eingang. Its own action because it is a DELETE, with its own event server-side. */
        'Unassign Document': props<{ projectId: string; documentId: string }>(),

        /**
         * #84/F2 · F1 — the two write gestures #57's auto-assignment needs a human compensation for.
         * Verb first, like `Assign Document` / `Unassign Document`. NO `…Success` action: a success
         * answers with the updated document and runs through `loadDocumentSuccess` + `loadDocuments`
         * exactly like `assignDocument$` — both surfaces (row pill, sidebar) need refreshing, and a
         * third action here would be a second place that "success" could mean.
         */
        'Accept Proposal': props<{ projectId: string; documentId: string; accountId?: string }>(),
        'Reject Proposal': props<{ projectId: string; documentId: string }>(),
        /**
         * F5 — the write failure of accept/reject AND of `Unassign Document` (#84 extends the
         * un-filing path to the same action, for the identical reason). Carries only the document id:
         * the row/sidebar that failed is the row/sidebar that renders the retry, and `documentId` is
         * all the reducer needs to know which one that is.
         */
        'Proposal Failure': props<{ documentId: string }>(),
        /** Dauerakte on its own — the single-document page's standing panel changes only this. */
        'Set Document Validity': props<{
            projectId: string;
            documentId: string;
            validity: 'closing' | 'longTerm';
        }>(),

        /**
         * Pull the bound SharePoint folders into the closing — the hub's "Jetzt synchronisieren", and
         * the automatic pull when the settings modal closes after folders were picked.
         *
         * ⚠️ NOT dispatched on hub load, by product decision (2026-08-19): opening the tab reads
         * `closing_documents` only and never calls Microsoft. Otherwise the tab's load time would
         * depend on someone else's SharePoint on every single navigation.
         */
        'Sync Sharepoint': props<{ projectId: string }>(),
        /**
         * `created: 0` with a non-empty `skipped` is the NORMAL result of syncing twice — carried so
         * the UI can say "nothing new" instead of leaving the user wondering whether it worked.
         */
        'Sync Sharepoint Success': props<{ result: ISharepointFetchResponse }>(),
        'Sync Sharepoint Failure': emptyProps(),

        // --- REV-21964 - R3/U1: der ASYNCHRONE Lauf ---------------------------------------------
        //
        // Der Knopf wartet nicht mehr auf das Ergebnis. Fuenfhundert Dateien passen nicht in einen
        // Request: die Aktion oben importiert INNERHALB der Anfrage, was bei zehn Dokumenten
        // unsichtbar ist und beim erlaubten Maximum in einen Timeout laeuft - der Nutzer sieht einen
        // Fehler, waehrend der Import serverseitig weiterlaeuft und die schon erzeugten Dokumente
        // unsichtbar bleiben. Sie bleibt trotzdem bestehen: der Ordner-Anbindungs-Ausloeser nutzt sie.

        /** "Jetzt synchronisieren" - startet einen Lauf und kommt sofort mit einer Lauf-Id zurueck. */
        'Start Sharepoint Sync': props<{ projectId: string }>(),
        /**
         * Die Antwort auf den Start - angenommen ODER abgewiesen, beides mit HTTP 200.
         *
         * WARNUNG: Eine Abweisung ist KEIN Fehler und darf nicht ueber `...Failure` laufen.
         * `alreadyRunning` traegt sogar die Id des laufenden Laufs, sodass die UI ihm einfach folgen
         * kann, statt eine Fehlermeldung ueber einem Lauf anzuzeigen, der gerade Dokumente importiert.
         */
        'Start Sharepoint Sync Result': props<{ result: ISyncStartResult }>(),
        'Start Sharepoint Sync Failure': emptyProps(),

        /**
         * Einen Lauf nachlesen. Auch der EINSTIEG: der Hub laedt beim Oeffnen den letzten Lauf, ohne
         * dessen Id zu kennen - `runId: null` fragt nach dem juengsten.
         *
         * WARNUNG: Das ist kein Griff nach Microsoft. Es liest unsere eigene Zeile; die
         * Produktentscheidung "der Reiter ruft beim Oeffnen nie Microsoft" bleibt unberuehrt.
         */
        'Load Sync Run': props<{ projectId: string; runId: string | null }>(),
        /** `run: null` heisst "diese Abrechnung wurde nie synchronisiert" - ein Leerzustand. */
        'Load Sync Run Success': props<{ run: ISyncRun | null }>(),
        /**
         * Der Bericht konnte nicht gelesen werden. Absichtlich OHNE `error`-Flag im Slice: die
         * Dokumentenliste steht, und ein nicht lesbarer Bericht darf dem Nutzer nicht die Tabelle
         * wegnehmen, auf die er gerade schaut - dieselbe Trennung wie F5 bei den Schreibfehlern.
         */
        'Load Sync Run Failure': emptyProps(),
        /**
         * Ein Lauf ist fertig (`completed`/`failed`) - beendet das Nachfragen und laedt die Liste neu.
         */
        'Sync Run Settled': props<{ run: ISyncRun }>(),

        /**
         * Pure UI state, but it lives in the slice because the hub is a CONTROLLED pure view: it
         * renders `activeFilter`/`sort` from its `@Input()`s and never mutates them, so an unanswered
         * click leaves the pill visibly stuck. The reducer answering is what makes it move.
         */
        'Set Filter': props<{ filter: DocumentFilterKey }>(),
        /**
         * A column header click. Reloads: the ORDER is the server's answer now, not a local compare.
         * `null` is the third click — back to the server's own order (newest first).
         */
        'Set Sort': props<{ sort: DocumentSort | null }>(),
        /**
         * Every keystroke in the header's search field. Reloads the list DEBOUNCED, because the term
         * is a server query now: it recomputes the three counts over the whole closing.
         */
        'Set Search Term': props<{ searchTerm: string }>(),
        /**
         * REV-21901 N3/Weg 1 (W2) — the "automatisch zugewiesen" facet toggle. SLICE state like
         * `activeFilter`, no longer page-local: the facet is a server parameter now
         * (`assignedBy=agent`), and the reload paths that read everything on screen from the store
         * (`Reload Documents` — remount, SharePoint modal) would silently un-narrow a page-local
         * flag. Reloads like `Set Filter`: which rows exist is the server's decision.
         */
        'Set Agent Only': props<{ agentOnly: boolean }>(),
    },
});
